import { useState, useRef, useEffect } from "react";
import { CheckIcon, ChevronDownIcon } from "../../../assets/icons";
import { SelectableItem } from "../selectable-item/SelectableItem";
import styles from "./ExpendableSelector.module.scss";

// ─── InitialsAvatar ──────────────────────────────────────────────────────────
// Mirrors Flutter's InitialsAvatar widget (same palette + initials logic).

const AVATAR_PALETTE = ["#312F60", "#046C35", "#F38415"];

function initialsOf(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	return name.length > 0 ? name[0].toUpperCase() : "";
}

interface InitialsAvatarProps {
	name: string;
	index: number;
	size?: number;
	fontSize?: number;
}

function InitialsAvatar({ name, index, size = 36, fontSize = 13 }: InitialsAvatarProps) {
	const color = AVATAR_PALETTE[index % AVATAR_PALETTE.length];
	return (
		<span
			className={styles.avatar}
			style={{ width: size, height: size, minWidth: size, background: color, fontSize }}
		>
			{initialsOf(name)}
		</span>
	);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExpendableSelectorItem<T = unknown> {
	id: string;
	title: string;
	subtitle?: string;
	isSelected?: boolean;
	isLoading?: boolean;
	data?: T;
}

interface ExpendableSelectorProps<T = unknown> {
	title: string;
	items: ExpendableSelectorItem<T>[];
	onItemSelected: (item: ExpendableSelectorItem<T>) => void;
	bottomActionText?: string;
	bottomActionIcon?: React.ReactNode;
	onBottomActionPressed?: () => void;
}

// ─── ExpendableSelector ───────────────────────────────────────────────────────

export function ExpendableSelector<T = unknown>({
	title,
	items,
	onItemSelected,
	bottomActionText,
	bottomActionIcon,
	onBottomActionPressed,
}: ExpendableSelectorProps<T>) {
	const [isExpanded, setIsExpanded] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = useState(0);

	useEffect(() => {
		if (contentRef.current) {
			setContentHeight(contentRef.current.scrollHeight);
		}
	}, [items, bottomActionText]);

	const toggle = () => setIsExpanded((prev) => !prev);

	// Collapsed preview: up to 3 avatars + optional "+N"
	const MAX_COLLAPSED = 3;
	const visibleItems = items.slice(0, MAX_COLLAPSED);
	const extra = items.length - MAX_COLLAPSED;

	return (
		<div className={styles.container}>
			{/* Header */}
			<button className={styles.header} onClick={toggle} aria-expanded={isExpanded}>
				<span className={styles.headerTitle}>{title}</span>

				{!isExpanded && (
					<span className={styles.collapsedAvatars}>
						{visibleItems.map((item, i) => (
							<InitialsAvatar key={item.id} name={item.title} index={i} size={24} fontSize={10} />
						))}
						{extra > 0 && <span className={styles.extraCount}>+{extra}</span>}
					</span>
				)}

				<span className={`${styles.chevron} ${isExpanded ? styles.chevronUp : ""}`}>
					<ChevronDownIcon />
				</span>
			</button>

			{/* Expandable list */}
			<div
				className={styles.expandable}
				style={{ maxHeight: isExpanded ? contentHeight : 0 }}
			>
				<div ref={contentRef}>
					<div className={styles.divider} />

					{items.map((item, index) => {
						const isLast = index === items.length - 1 && !bottomActionText;
						return (
							<div
								key={item.id}
								className={`${styles.itemWrapper} ${item.isLoading ? styles.itemLoading : ""}`}
							>
								<SelectableItem
									title={item.title}
									subtitle={item.subtitle ?? ""}
									leftIcon={<InitialsAvatar name={item.title} index={index} />}
									rightIcon={
										item.isLoading ? (
											<span className={styles.spinner} />
										) : item.isSelected ? (
											<CheckIcon className={styles.checkIcon} />
										) : <></>
									}
									hideBottomBorder={isLast}
									onClick={() => onItemSelected(item)}
								/>
							</div>
						);
					})}

					{bottomActionText && (
						<SelectableItem
							title={bottomActionText}
							subtitle=""
							leftIcon={bottomActionIcon}
							rightIcon={<span />}
							hideBottomBorder
							onClick={onBottomActionPressed}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
